import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@repo/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Activity,
  Calendar as CalendarIcon,
  Clock,
  FileBarChart,
  LayoutDashboard,
  MessageCircle,
  Users,
} from 'lucide-react'

import { OnboardingChecklist } from '@/components/dashboard/onboarding-checklist'
import { stageLabel, STAGES, usePeriod } from '@/features/report/period'
import {
  AttendanceSummarySection,
  DatePicker,
  METRIC_HINTS,
  MetricCard,
  TimeMetricCard,
  VerticalBar,
} from '@/features/report/widgets'
import { useAuth } from '@/hooks/use-auth'
import {
  type DashboardStage,
  useAttendanceSummary,
  useDashboardAnalytics,
} from '@/hooks/use-dashboard-stats'

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardPage,
})

function DashboardPage() {
  const { currentOrgId } = useAuth()
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    stageFilter,
    setStageFilter,
    periodState,
    filters,
    prevFilters,
  } = usePeriod()

  const dashboard = useDashboardAnalytics(currentOrgId, filters)
  const prevDashboard = useDashboardAnalytics(currentOrgId, prevFilters)
  const attendanceSummary = useAttendanceSummary(currentOrgId, {
    from: filters.from,
    to: filters.to,
  })
  const prevAttendance = useAttendanceSummary(currentOrgId, {
    from: prevFilters.from,
    to: prevFilters.to,
  })

  const data = dashboard.data
  const prev = prevDashboard.data
  const summary = attendanceSummary.data
  const prevSummary = prevAttendance.data
  const funnelMax = Math.max(1, ...(data?.funnel ?? []).map((r) => r.count))

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={LayoutDashboard}
        title="Dashboard"
        description="Visão geral da operação comercial e de atendimento"
        actions={
          <>
            <Select
              value={period}
              onValueChange={(value) => setPeriod(value as typeof period)}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 90 dias</SelectItem>
                <SelectItem value="custom">Personalizado</SelectItem>
              </SelectContent>
            </Select>

            {period === 'custom' ? (
              <>
                <DatePicker
                  label="De"
                  value={customFrom}
                  onChange={setCustomFrom}
                />
                <DatePicker
                  label="Até"
                  value={customTo}
                  onChange={setCustomTo}
                />
              </>
            ) : null}

            <Select
              value={stageFilter}
              onValueChange={(value) => setStageFilter(value as DashboardStage)}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Estágio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os estágios</SelectItem>
                {STAGES.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {stageLabel(stage)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button asChild variant="outline">
              <Link to="/relatorio">
                <FileBarChart className="h-4 w-4" />
                Relatório
              </Link>
            </Button>
          </>
        }
      />

      <main className="min-h-0 flex-1 overflow-y-auto p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="secondary">{periodState.label}</Badge>
          {stageFilter !== 'all' ? (
            <Badge variant="outline">{stageLabel(stageFilter)}</Badge>
          ) : null}
        </div>

        {dashboard.isLoading || !data ? (
          <DashboardSkeleton />
        ) : (
          <div className="space-y-6">
            <OnboardingChecklist orgId={currentOrgId} />

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard
                label="Conversas abertas"
                value={summary?.totalOpen ?? data.activeLeads}
                hint={METRIC_HINTS.conversasAbertas}
                icon={<MessageCircle className="h-4 w-4" />}
              />
              <MetricCard
                label="Finalizadas"
                value={summary?.totalFinalized ?? 0}
                previous={prevSummary?.totalFinalized ?? null}
                hint={METRIC_HINTS.finalizadas}
                icon={<Activity className="h-4 w-4" />}
              />
              <MetricCard
                label="Leads ativos em conversa"
                value={data.activeLeads}
                previous={prev?.activeLeads ?? null}
                hint={METRIC_HINTS.leadsAtivos}
                icon={<Users className="h-4 w-4" />}
              />
              <MetricCard
                label="Reuniões agendadas"
                value={data.appointmentsCount}
                previous={prev?.appointmentsCount ?? null}
                hint={METRIC_HINTS.reunioes}
                icon={<CalendarIcon className="h-4 w-4" />}
              />
              <TimeMetricCard
                label="1ª resposta (médio)"
                valueMs={summary?.avgFirstResponseMs ?? null}
                hint={METRIC_HINTS.primeiraResposta}
                icon={<Clock className="h-4 w-4" />}
              />
              <TimeMetricCard
                label="Tempo médio resposta"
                valueMs={summary?.avgResponseMs ?? null}
                hint={METRIC_HINTS.tempoResposta}
                icon={<Clock className="h-4 w-4" />}
              />
            </div>

            {summary ? <AttendanceSummarySection summary={summary} /> : null}

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Volume por funil</CardTitle>
                <p className="text-muted-foreground text-xs">
                  Distribuição dos leads no período selecionado
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex h-48 items-end gap-3">
                  {data.funnel.map((row) => (
                    <VerticalBar
                      key={row.stage}
                      label={stageLabel(row.stage)}
                      value={row.count}
                      max={funnelMax}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-28" />
        ))}
      </div>
      <Skeleton className="h-24" />
    </div>
  )
}
